import withStore, {
  TPresentable,
  TLoadingAwarePresenter
} from "../../core/presenter/withStore";
import Application from "../../Application";
import ShowSuccessMessageInteractor from "../../core/interactor/notifications/ShowSuccessMessageInteractor";
import { IOrganisationIndexCategory } from "../model/OrganisationIndexCategory";
import AddOrganisationLegalViewTypesInteractor from "../interactors/AddOrganisationLegalViewTypesInteractor";
import GetOrganisationLegalViewTypesInteractor from "../interactors/GetOrganisationLegalViewTypesInteractor";
import GetLegalViewTypeIndexCategoriesInteractor from "../interactors/GetLegalViewTypeIndexCategoriesInteractor";
import AddLegalViewTypeIndexCategoriesInteractor from "../interactors/AddLegalViewTypeIndexCategoriesInteractor";
import DocumentTypeIndexCategory, {
  IDocumentTypeIndexCategory
} from "../../documentTypes/model/DocumentTypeIndexCategory";
import { IDocumentType } from "../../documentTypes/model/DocumentType";
import { IVisibilityLevel } from "../../documentTypes/model/VisibilityLevel";
import GetAllLegalViewTypesInteractor from "../../legalViews/interactor/GetAllLegalViewTypesInteractor";
import RemoveDocumentLegalViewTypeInteractor from "../interactors/RemoveDocumentLegalViewTypeInteractor";

export interface TOrganisationLegalViewTypesPresenter
  extends TLoadingAwarePresenter {
  organisationId: number;
  organisationLegalViewTypes: IDocumentType[];
  legalViewTypes: IDocumentType[];
  modalVisible: boolean;
  assignTypesToOrganisationModalVisible: boolean;
  currentLegalViewTypeId?: number;
  organisationIndexCategories: IOrganisationIndexCategory[];
  legalViewTypeIndexCategories: IDocumentTypeIndexCategory[];
  codeList: number[];
}

export interface IOrganisationLegalViewTypesPresenter
  extends TOrganisationLegalViewTypesPresenter,
    TPresentable {
  loadReferenceValues(
    organisationId: number,
    organisationIndexCategories: IOrganisationIndexCategory[],
    legalViewTypes: IDocumentType[],
    organisationLegalViewTypes: IDocumentType[]
  ): void;
  onChangeAddElementsModalVisible(value: boolean, itemId?: number): void;
  addLegalViewTypeToOrganisation(data: any): void;
  onChangeAddTypesToOrganisationModalVisible(value: boolean): void;
  getLegalViewTypesForOrganisation(): void;
  loadOrganisationLegalViewTypes(
    organisationLegalViewTypes: IDocumentType[],
    legalViewTypes: IDocumentType[]
  ): void;
  getLegalViewTypeIndexCategories(legalViewTypeId: number): void;
  addLegalViewTypeIndexCategories(items: any): void;
  setOrganisationLegalViewTypeData(id: number): void;
  removeDocumentLegalType(id: number): void;
}

const defaultState: TOrganisationLegalViewTypesPresenter = {
  organisationId: NaN,
  organisationLegalViewTypes: [],
  modalVisible: false,
  currentLegalViewTypeId: undefined,
  codeList: [],
  legalViewTypes: [],
  assignTypesToOrganisationModalVisible: false,
  organisationIndexCategories: [],
  legalViewTypeIndexCategories: []
};

const OrganisationLegalViewTypesPresenter = withStore<
  IOrganisationLegalViewTypesPresenter,
  TOrganisationLegalViewTypesPresenter
>(
  ({
    application,
    store,
    loader,
    translate
  }): IOrganisationLegalViewTypesPresenter => {
    const _store = store;
    const _application: Application = application;
    const state = _store.getState<
      TOrganisationLegalViewTypesPresenter & TLoadingAwarePresenter
    >();
    loader.start("organisationLegalViewType");
    const loadOrganisationLegalViewTypes = (
      organisationLegalViewTypes: IDocumentType[],
      legalViewTypes: IDocumentType[]
    ) => {
      _store.update({
        organisationLegalViewTypes,
        legalViewTypes: legalViewTypes.filter(
          (item) =>
            !organisationLegalViewTypes.map((el) => el.id).includes(item.id)
        )
      });
    };
    const loadReferenceValues = (
      organisationId: number,
      organisationIndexCategories: IOrganisationIndexCategory[],
      legalViewTypes: IDocumentType[],
      organisationLegalViewTypes: IDocumentType[]
    ) => {
      _store.update({
        legalViewTypes: legalViewTypes.filter(
          (item) =>
            !organisationLegalViewTypes.map((el) => el.id).includes(item.id)
        ),
        organisationIndexCategories,
        organisationId,
        organisationLegalViewTypes
      });
      loader.stop("organisationLegalViewType");
    };
    const getLegalViewTypeIndexCategories = async (legalViewTypeId: number) => {
      const { organisationId } =
        _store.getState<TOrganisationLegalViewTypesPresenter>();
      let legalViewTypeIndexCategories = await _application.container
        .resolve<GetLegalViewTypeIndexCategoriesInteractor>(
          "getLegalViewTypeIndexCategories"
        )
        .execute(legalViewTypeId, organisationId);
      _store.update({ legalViewTypeIndexCategories });
    };
    const addLegalViewTypeIndexCategories = async (items: any) => {
      const { currentLegalViewTypeId, organisationId } =
        _store.getState<TOrganisationLegalViewTypesPresenter>();
      var itemList = items.fields?.map((item) =>
        DocumentTypeIndexCategory(item)
      );
      currentLegalViewTypeId &&
        (await _application.container
          .resolve<AddLegalViewTypeIndexCategoriesInteractor>(
            "addLegalViewTypeIndexCategories"
          )
          .execute(itemList, currentLegalViewTypeId, organisationId));
      _application.container
        .resolve<ShowSuccessMessageInteractor>("showSuccessMessage")
        .execute(translate("editors.project.create_success_message"));
    };

    const onChangeAddElementsModalVisible = async (
      value: boolean,
      itemId?: number
    ) => {
      _store.update({
        modalVisible: value
      });
      if (itemId) {
        await getLegalViewTypeIndexCategories(itemId);

        _store.update({ currentLegalViewTypeId: itemId });
      } else {
        _store.update({
          legalViewTypeIndexCategories: [],
          currentLegalViewTypeId: undefined
        });
      }
    };
    const onChangeAddTypesToOrganisationModalVisible = async (
      value: boolean
    ) => {
      _store.update({
        assignTypesToOrganisationModalVisible: value
      });
    };

    const addLegalViewTypeToOrganisation = async (item: IDocumentType) => {
      loader.start("organisationLegalViewType");
      const { organisationId } =
        _store.getState<TOrganisationLegalViewTypesPresenter>();

      item.visibilityLevels = item.visibilityLevels.map((elem) => {
        return {
          status: elem.status,
          valid: true,
          visibilityLevel: elem.visibilityLevel,
          organisationId: organisationId,
          documentTypeId: item.id
        } as IVisibilityLevel;
      });

      await _application.container
        .resolve<AddOrganisationLegalViewTypesInteractor>(
          "addOrganisationLegalViewTypes"
        )
        .execute(item, organisationId);
      _application.container
        .resolve<ShowSuccessMessageInteractor>("showSuccessMessage")
        .execute(translate("editors.project.create_success_message"));

      getLegalViewTypesForOrganisation();
      loader.stop("organisationLegalViewType");
      return true;
    };
    const removeDocumentLegalType = async (id: number) => {
      loader.start("organisationLegalViewType");
      const { organisationId } =
        _store.getState<TOrganisationLegalViewTypesPresenter>();
      await _application.container
        .resolve<RemoveDocumentLegalViewTypeInteractor>(
          "removeDocumentLegalViewType"
        )
        .execute(organisationId, id);
      _application.container
        .resolve<ShowSuccessMessageInteractor>("showSuccessMessage")
        .execute(translate("editors.project.create_success_message"));
      getLegalViewTypesForOrganisation();
      loader.stop("organisationLegalViewType");
      return true;
    };
    const setOrganisationLegalViewTypeData = async (id: number) => {
      const { organisationId, codeList } =
        _store.getState<TOrganisationLegalViewTypesPresenter>();
      if (!codeList.includes(id)) {
        codeList.push(id);
      } else {
        codeList.splice(codeList.indexOf(id), 1);
      }
      _store.update({ codeList, organisationId });
    };

    const getLegalViewTypesForOrganisation = async () => {
      let organisationId =
        _store.getState<TOrganisationLegalViewTypesPresenter>().organisationId;
      loader.start("organisationLegalViewType");
      let legalViewTypesForOrganisation = await _application.container
        .resolve<GetOrganisationLegalViewTypesInteractor>(
          "getOrganisationLegalViewTypes"
        )
        .execute(organisationId);
      let legalViewTypes = await _application.container
        .resolve<GetAllLegalViewTypesInteractor>("getAllLegalViewTypes")
        .execute();

      loadOrganisationLegalViewTypes(
        legalViewTypesForOrganisation,
        legalViewTypes
      );
    };
    return {
      ...state,
      store: _store,
      loader,
      application: _application,
      translate,
      loadReferenceValues,
      onChangeAddElementsModalVisible,
      onChangeAddTypesToOrganisationModalVisible,
      addLegalViewTypeToOrganisation,
      getLegalViewTypesForOrganisation,
      setOrganisationLegalViewTypeData,
      loadOrganisationLegalViewTypes,
      getLegalViewTypeIndexCategories,
      addLegalViewTypeIndexCategories,
      removeDocumentLegalType
    };
  },
  defaultState
);

export default OrganisationLegalViewTypesPresenter;
